/**
 * artificialFill.ts - the globe's artificial imagery, composited over ground
 * nobody measured.
 *
 * `tools/dtm/fill_artificial.py` writes a second equirect map per body,
 * `public/art/atlas/planets/<id>_artificial_2k.webp`, whose alpha is 255 where
 * the fill HAS data and 0 where it does not - which is every texel an
 * instrument measured. The chunk here mixes it in on that alpha alone, so a
 * measured texel is the identity whatever the switch says, and `fxArtOn` at 0
 * is a bit-identical frame.
 *
 * It lands straight after `map_fragment` and BEFORE `coverageFill.ts`'s chunk:
 * coverage keys on the measured alpha and paints what is STILL unfilled, so a
 * control armed over a body's holes (`descent-dojo --coverage --inject
 * nodata-black`) keeps winning over the fill rather than reading through it.
 */

import * as THREE from 'three';

import {
  applyArtificialRelief, reliefKey, type ArtificialReliefRef,
} from './artificialRelief.ts';
import { applyCoverageFill, type CoverageFillRef } from './coverageFill.ts';
import { applyBodyShadow, bodyShadowKey, bodyShadowRefOf } from './bodyShadow.ts';
import {
  albedoFillTransferOf, albedoTransferRefOf, albedoTransferUniform, applyAlbedoTransfer, type AlbedoTransfer,
} from './albedoTransfer.ts';

/** The two uniforms the chunk reads, owned by whoever drew the body. */
export interface ArtificialFillRef {
  readonly map: { value: THREE.Texture | null };
  /** 1 only while the layer is on AND this body's map has landed. */
  readonly on: { value: number };
  /**
   * `descent-dojo --artificial --inject art-over-measured`: the control.
   *
   * At 1 the mix ignores the map's alpha and paints the fill over measured
   * ground too, which is the one thing this role must never do. The bar that
   * says a measured texel did not move has to be able to read it moving.
   */
  readonly force: { value: number };
  /**
   * The body's linear-albedo decode (`albedoTransfer.ts`'s uniform layout,
   * z 0 for a colour body): the fill is written in the mosaic's own bytes, so
   * on a greyscale-DN body it takes the ground's decode and not the sRGB one.
   */
  readonly dn: { value: THREE.Vector3 };
}

export interface ArtificialFillHandle {
  readonly ref: ArtificialFillRef;
  /** The Layers row. */
  setOn(on: boolean): void;
  /** The map itself, or null while it is in flight or failed. */
  setMap(map: THREE.Texture | null): void;
  /** The control: paint over measured ground too. */
  setOverMeasured(on: boolean): void;
  /**
   * The other control: fetch the map, switch the row, composite nothing.
   *
   * The frame it leaves is the one the build before this role drew, since `on`
   * at 0 makes the mix its own identity - which is the build the `gap` bar has
   * to be able to read as red.
   */
  setInert(on: boolean): void;
  /** The decode the body's own ground takes; null for a colour body. */
  setTransfer(t: AlbedoTransfer | null): void;
  /** Whether the frame is drawing it, for a reader that has to report what it draws. */
  drawn(): boolean;
}

/**
 * `perDrawMap` is the tile band: there the map is one texture per tile and
 * this handle owns the switch alone, so `on` cannot gate on a map it will
 * never hold. A globe owns both and gates on its own.
 *
 * `load` is called once, the first time the layer asks for this fill.
 *
 * The map is the layer's own asset and nothing else reads it, so a profile
 * with the row switched off makes no request for it at all - which is also the
 * reading `descent-dojo --artificial` takes off the network.
 */
export function createArtificialFill(perDrawMap = false, load?: () => void): ArtificialFillHandle {
  const ref: ArtificialFillRef = {
    map: { value: null }, on: { value: 0 }, force: { value: 0 }, dn: albedoTransferUniform(null, false),
  };
  let layerOn = false;
  let asked = false;
  let inert = false;
  function sync(): void {
    ref.on.value = layerOn && !inert && (perDrawMap || ref.map.value !== null) ? 1 : 0;
  }
  return {
    ref,
    setOn(on: boolean): void {
      layerOn = on;
      if (on && !asked && load) { asked = true; load(); }
      sync();
    },
    setMap(map: THREE.Texture | null): void { ref.map.value = map; sync(); },
    setOverMeasured(on: boolean): void { ref.force.value = on ? 1 : 0; },
    setInert(on: boolean): void { inert = on; sync(); },
    setTransfer(t: AlbedoTransfer | null): void {
      const live = albedoFillTransferOf(t);
      ref.dn.value.set(live?.dnZero ?? 0, live?.perDn ?? 0, live ? 1 : 0);
    },
    drawn(): boolean { return ref.on.value > 0; },
  };
}

/**
 * The composite itself: one line, wherever it is injected.
 *
 * `fxArtOn` at 0 is a bit-identical frame and the map's own alpha is 0 on
 * every measured texel, so a measured texel is the identity whatever the
 * switch says. `fxArtAll` is the control that breaks exactly that.
 */
const ART_MIX = '  diffuseColor.rgb = mix(diffuseColor.rgb, fxArtLinear(fxArt.rgb),\n'
  + '    fxArtOn * max(fxArt.a, fxArtAll));';

// On a linear-reflectance body the fill's sampled value is an sRGB decode of the
// mosaic's byte; put the byte back and take the ground's own decode.
const ART_DECL = 'uniform sampler2D fxArtMap;\nuniform float fxArtOn;\nuniform float fxArtAll;\n'
  + 'uniform vec3 fxArtDn;\n'
  + 'vec3 fxArtLinear(vec3 c) {\n'
  + '  if (fxArtDn.z < 0.5) return c;\n'
  + '  vec3 s = mix(1.055 * pow(c, vec3(0.4166667)) - 0.055, c * 12.92, step(c, vec3(0.0031308)));\n'
  + '  return clamp(fxArtDn.y * (s * 255.0 - fxArtDn.x), 0.0, 1.0);\n'
  + '}\n';

/**
 * Inject the chunk into a `MeshStandardMaterial`'s compiled shader.
 *
 * `shaped` recovers the same direction-sampled uv `SHAPE_MAP_FRAGMENT` reads,
 * gradients and antimeridian fix included: a displaced globe samples its map by
 * direction, and this map read through the uv fallback would sit a seam of
 * artificial ground down the antimeridian of a body whose measured ground has
 * none.
 */
export function applyArtificialFill(shader: THREE.WebGLProgramParametersWithUniforms,
  ref: ArtificialFillRef, shaped: boolean): void {
  shader.uniforms['fxArtMap'] = ref.map;
  shader.uniforms['fxArtOn'] = ref.on;
  shader.uniforms['fxArtAll'] = ref.force;
  shader.uniforms['fxArtDn'] = ref.dn;
  const read = shaped ? directionSampledArtRead('vShapeDir') : '  vec4 fxArt = texture2D(fxArtMap, vMapUv);\n';
  shader.fragmentShader = ART_DECL
    + `${shader.fragmentShader}`
      .replace('#include <map_fragment>', `#include <map_fragment>\n${read}${ART_MIX}`);
}

/**
 * The direction-sampled uv read, antimeridian fix included, as a fragment
 * chunk that reads a vec3 varying by name.
 *
 * Shared between the globe's own `shaped` read (`vShapeDir`) and a tile's
 * globe-fallback read (`vArtFallbackDir`, below) so the two cannot drift
 * apart - a tile with no ancestor's own `.art.webp` has to sample the same
 * equirect map the same way the globe itself does.
 */
function directionSampledArtRead(dirVarying: string): string {
  return `  vec3 fxArtDir = normalize(${dirVarying});\n`
    + '  vec2 fxArtUv = vec2(\n'
    + '    0.5 + atan(-fxArtDir.z, fxArtDir.x) * 0.1591549430918953,\n'
    + '    0.5 + asin(clamp(fxArtDir.y, -1.0, 1.0)) * 0.3183098861837907);\n'
    + '  vec2 fxArtDx = dFdx(fxArtUv);\n'
    + '  vec2 fxArtDy = dFdy(fxArtUv);\n'
    + '  if (abs(fxArtDx.x) > 0.5) fxArtDx.x -= sign(fxArtDx.x);\n'
    + '  if (abs(fxArtDy.x) > 0.5) fxArtDy.x -= sign(fxArtDy.x);\n'
    + '  vec4 fxArt = textureGrad(fxArtMap, fxArtUv, fxArtDx, fxArtDy);\n';
}

/**
 * The same chunk on a streamed tile, sampled through its own uv attribute.
 *
 * `uvArt` and not `uv`: the fill's floor is shallower than the albedo's (pluto
 * fills to L3 and images to L4), so a tile at or below the albedo floor draws
 * its own measured texture while borrowing invented ground from an ancestor
 * further up, and the two rectangles are not the same rectangle. Reading the
 * albedo's `vMapUv` here tiled the fill four times across an L4 leaf.
 *
 * Injected AFTER `applyCoverageFill` for the tile, which is what puts it
 * BEFORE coverage in the drawn order: both substitutions keep the
 * `#include <map_fragment>` tag, so the later replace splices between the tag
 * and the earlier chunk's lines. Coverage therefore still keys on the measured
 * alpha and still paints over the fill, which is what keeps
 * `--inject nodata-black` and `--inject fill-row` winning.
 */
export function applyTileArtificialFill(shader: THREE.WebGLProgramParametersWithUniforms,
  ref: ArtificialFillRef): void {
  shader.uniforms['fxArtMap'] = ref.map;
  shader.uniforms['fxArtOn'] = ref.on;
  shader.uniforms['fxArtAll'] = ref.force;
  shader.uniforms['fxArtDn'] = ref.dn;
  shader.vertexShader = `attribute vec2 uvArt;\nvarying vec2 vArtUv;\n${shader.vertexShader}`
    .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vArtUv = uvArt;');
  shader.fragmentShader = `${ART_DECL}varying vec2 vArtUv;\n${shader.fragmentShader}`
    .replace('#include <map_fragment>',
      '#include <map_fragment>\n  vec4 fxArt = texture2D(fxArtMap, vArtUv);\n' + ART_MIX);
}

/**
 * The globe's own fill, composited on a tile whose ancestor chain streamed
 * none - a deployed bucket that predates `sync_tiles.py --role artificial`
 * serves a pyramid with no artificial block at all, and a tile drawn against
 * it would otherwise fall through to the bake's flat `unmeasured_rgb` with no
 * fill on it whatsoever above OR below the streaming edge.
 *
 * `origin` is the tile mesh's own centre, atlas frame, the same one
 * `bodyToAtlas` builds for `mesh.position`: `position + fxArtOrigin`
 * reconstructs the vertex's absolute atlas-frame offset from the body's
 * centre, which normalizes to the same direction the globe's own `shaped`
 * read takes from `vShapeDir`. The fill is coarser than a streamed
 * `.art.webp` - it is the 2k globe map, not this tile's own bake - but it is
 * the same map the globe draws above the edge, so the seam at the edge is a
 * resolution step and not a hole.
 */
export function applyTileArtificialFallback(shader: THREE.WebGLProgramParametersWithUniforms,
  ref: ArtificialFillRef, origin: THREE.Vector3): void {
  shader.uniforms['fxArtMap'] = ref.map;
  shader.uniforms['fxArtOn'] = ref.on;
  shader.uniforms['fxArtAll'] = ref.force;
  shader.uniforms['fxArtDn'] = ref.dn;
  shader.uniforms['fxArtOrigin'] = { value: origin };
  shader.vertexShader = `uniform vec3 fxArtOrigin;\nvarying vec3 vArtFallbackDir;\n${shader.vertexShader}`
    .replace('#include <begin_vertex>',
      '#include <begin_vertex>\n  vArtFallbackDir = position + fxArtOrigin;');
  shader.fragmentShader = `${ART_DECL}varying vec3 vArtFallbackDir;\n${shader.fragmentShader}`
    .replace('#include <map_fragment>',
      `#include <map_fragment>\n${directionSampledArtRead('vArtFallbackDir')}${ART_MIX}`);
}

/** The program cache key's own suffix: three's does not vary with a hook's content. */
export function artificialKey(ref: ArtificialFillRef | null, shaped: boolean): string {
  return ref === null ? '' : shaped ? '-art1s' : '-art1';
}

/** Userdata key: which fill this material composites, for the one writer of its hook. */
const ARTIFICIAL_REF = 'fxcivArtificialRef';

/**
 * Say which artificial fill this globe carries.
 *
 * On the material rather than threaded through every hook writer, the way
 * `shapeGlobe.ts` marks a direction-sampled map: three keeps ONE
 * `onBeforeCompile` per material and `src/atlas.ts` rewrites it on every focus
 * change, so what it has to put back has to be readable from the material it is
 * rewriting. Unlike the focus and the air mount, this one never changes over
 * the material's life.
 */
export function markArtificialFill(mat: THREE.Material, ref: ArtificialFillRef): void {
  mat.userData[ARTIFICIAL_REF] = ref;
}

export function artificialRefOf(mat: THREE.Material): ArtificialFillRef | null {
  const ref = mat.userData[ARTIFICIAL_REF] as ArtificialFillRef | undefined;
  return ref ?? null;
}

/**
 * The unfocused globe's whole program: the coverage fill, then this one.
 *
 * Coverage goes in first so this one lands INSIDE it: both keep the
 * `#include <map_fragment>` tag in what they substitute, so the second replace
 * splices between the tag and the first chunk's lines. The drawn order is
 * therefore the diffuse read, then the artificial mix, then coverage's paint
 * over whatever is still unfilled.
 */
export function globeFillChunks(material: THREE.Material, coverage: CoverageFillRef,
  artificial: ArtificialFillRef | null, relief: ArtificialReliefRef | null = null): void {
  const albedoDn = albedoTransferRefOf(material);
  const shadow = bodyShadowRefOf(material);
  material.onBeforeCompile = (shader): void => {
    applyCoverageFill(shader, coverage);
    if (artificial !== null) applyArtificialFill(shader, artificial, false);
    if (relief !== null) applyArtificialRelief(shader, relief);
    if (albedoDn !== null) applyAlbedoTransfer(shader, albedoDn);
    if (shadow !== null) applyBodyShadow(shader, shadow);
  };
  // A distinct key, or this material silently shares one compiled without the
  // chunk - three's own key does not vary with `onBeforeCompile`'s content.
  material.customProgramCacheKey = (): string =>
    `fxciv-globe-cov1${artificialKey(artificial, false)}${reliefKey(relief)}${albedoDn ? '-adn1' : ''}`
    + bodyShadowKey(shadow);
  material.needsUpdate = true;
}
